import {
    CheckCircle,
    ContentCopyRounded,
    OpenInNew,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    FormControl,
    IconButton,
    InputAdornment,
    Link,
    OutlinedInput,
    Typography,
} from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import {
    deleteRequestAPIAccess,
    generateRequestAPIAccess,
    getRequestAPIAccess,
} from '../../../services/userServices'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
import PaperComponent from '../../Common/PaperComponent'
import PopupTitle from '../../Common/PopupTitle'

interface APITokenProps {
    handleClose: () => void
    userId?: number
    userName?: string
}

export interface NewAPITokenData {
    userId: string
    orgId: string
    token: string
    status: string
}

const APIToken = (props: APITokenProps): ReactElement => {
    const { t } = useTranslation()
    const { handleClose, userId, userName } = props

    const dispatch = useAppDispatch()

    const orgId = useAppSelector((state) => state.org.org?.id)

    const [APIToken, setAPIToken] = useState<string>('')

    const [showToken, setShowToken] = useState(false)

    const [copied, setCopied] = useState(false)

    const handleClickShowPassword = () => setShowToken((show) => !show)

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault()
    }

    const handleMouseUpPassword = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault()
    }

    const getRequestAPIToken = useCallback(async () => {
        dispatch(updateLoader(true))

        const newAPIToken = await getRequestAPIAccess(orgId || '', userId)

        if (newAPIToken) {
            setAPIToken(newAPIToken?.token || '')
        }
        dispatch(updateLoader(false))
    }, [dispatch, orgId, userId])

    const updateAPIAccessToken = async () => {
        dispatch(updateLoader(true))

        const newAPIToken = await generateRequestAPIAccess(orgId || '', userId)

        if (newAPIToken) {
            setAPIToken(newAPIToken?.token || '')
        }
        dispatch(updateLoader(false))
    }

    const removeRequestAPIToken = async () => {
        dispatch(updateLoader(true))

        const newAPIToken = await deleteRequestAPIAccess(orgId || '', userId)

        if (newAPIToken) {
            setAPIToken(newAPIToken?.token || '')
        }
        dispatch(updateLoader(false))
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(APIToken)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch (err) {
            console.error('Copy failed:', err)
        }
    }

    useEffect(() => {
        if (orgId) getRequestAPIToken()
    }, [orgId, getRequestAPIToken])

    return (
        <Dialog
            open={true}
            scroll="paper"
            PaperProps={{
                sx: {
                    width: '550px',
                },
            }}
            PaperComponent={PaperComponent}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
        >
            {/* UBQ-5844: shared title row instead of a hand-built one (kiwi clicks span#close-icon[@title]).
             */}
            <PopupTitle
                id="draggable-dialog-title"
                draggable
                onClose={() => handleClose()}
                closeId="close-icon"
                closeTitle={t('close')}
            >
                <span id="scroll-dialog-title">{t('manageApiToken')}</span>
            </PopupTitle>
            <DialogContent id="scroll-dialog-description" sx={{ pt: 0 }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            marginBottom: '20px',
                        }}
                    >
                        <Box sx={{ flex: '1' }}>
                            {userName && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#0009',
                                            fontSize: '14px',
                                            textWrap: 'nowrap',
                                        }}
                                    >
                                        {t('userName') + ' :'}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#000',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            textWrap: 'nowrap',
                                            marginLeft: '4px',
                                        }}
                                    >
                                        {` ${userName}`}
                                    </Typography>
                                </Box>
                            )}

                            <FormControl fullWidth>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#0009',
                                            fontSize: '14px',
                                        }}
                                        id="api-token-label"
                                    >
                                        {t('apiToken')}
                                    </Typography>
                                    {APIToken && (
                                        <Box
                                            id="copy-API-Token-icon"
                                            display="flex"
                                            alignItems="center"
                                            onClick={handleCopy}
                                            sx={{
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <IconButton
                                                aria-label={
                                                    copied
                                                        ? t('copied')
                                                        : t('copyApiToken')
                                                }
                                                title={
                                                    copied
                                                        ? t('copied')
                                                        : t('copyApiToken')
                                                }
                                            >
                                                {copied ? (
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize: '15px',
                                                        }}
                                                    />
                                                ) : (
                                                    <ContentCopyRounded
                                                        sx={{
                                                            fontSize: '15px',
                                                        }}
                                                    />
                                                )}
                                            </IconButton>

                                            <Typography
                                                fontSize={13}
                                                color={'text.secondary'}
                                                marginLeft={'-4px'}
                                            >
                                                {copied ? 'Copied' : 'Copy'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <OutlinedInput
                                    size="small"
                                    id="API-token-field"
                                    label=""
                                    value={APIToken}
                                    sx={{
                                        width: '100%',
                                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                            {
                                                display: 'none',
                                            },
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield',
                                        },
                                    }}
                                    placeholder={t('apiToken')}
                                    inputProps={{
                                        style: {
                                            fontSize: 14,
                                            opacity:
                                                APIToken !== '' &&
                                                APIToken !== null
                                                    ? '1'
                                                    : '0.4',
                                        },
                                        'aria-label': t('apiToken'),
                                        'aria-labelledby': 'api-token-label',
                                    }}
                                    type={showToken ? 'text' : 'password'}
                                    endAdornment={
                                        APIToken && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    id="show-API-Token-icon"
                                                    aria-label={
                                                        showToken
                                                            ? t('hideApiToken')
                                                            : t('showApiToken')
                                                    }
                                                    title={
                                                        showToken
                                                            ? t('hideApiToken')
                                                            : t('showApiToken')
                                                    }
                                                    onClick={
                                                        handleClickShowPassword
                                                    }
                                                    onMouseDown={
                                                        handleMouseDownPassword
                                                    }
                                                    onMouseUp={
                                                        handleMouseUpPassword
                                                    }
                                                    edge="end"
                                                >
                                                    {showToken ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                />
                                <Link
                                    href={`https://${import.meta.env.VITE_ENV_PREFIX}-api.app.ubq.network/swagger-ui/index.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '13px',
                                        marginTop: '8px',
                                    }}
                                >
                                    {t('viewApiDocumentation')}
                                    <OpenInNew sx={{ fontSize: '14px' }} />
                                </Link>
                            </FormControl>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box>
                            <Button
                                sx={{ marginTop: '10px' }}
                                onClick={() => updateAPIAccessToken()}
                                variant="contained"
                                fullWidth
                                id="request-api-access-button"
                                aria-label={t('requestApiAccess')}
                                title={t('requestApiAccess')}
                            >
                                {t('requestApiAccess')}
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                sx={{ marginTop: '10px' }}
                                onClick={() => removeRequestAPIToken()}
                                variant="contained"
                                fullWidth
                                id="remove-api-access-button"
                                disabled={!APIToken}
                                aria-label={t('removeApiAccess')}
                                title={t('removeApiAccess')}
                            >
                                {t('removeApiAccess')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default APIToken
